import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  getCustomerPurchases,
  getRacketFreeEligibilityAdmin,
} from "../../../api/api";
import Pagination from "../../../components/layout/Pagination/Pagination";
import styles from "./ServiceDetails.module.css";

const ServiceDetails = () => {
  const navigate = useNavigate();
  const { customerId } = useParams();
  const location = useLocation();
  const { customerName } = location.state || {};

  const [serviceClaims, setServiceClaims] = useState([]);
  const [purchases, setPurchases] = useState([]);

  const [freeServices, setFreeServices] = useState(0);
  const [usedServices, setUsedServices] = useState(0);
  const [freeServicesTotal, setFreeServicesTotal] = useState(0);

  const [eligibilityMap, setEligibilityMap] = useState({});
  const [eligLoading, setEligLoading] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/login");
      return;
    }

    const fetchPurchases = async () => {
      try {
        const response = await getCustomerPurchases(customerId);
        const data = response?.data || {};

        const free = data.free_stringing_services || 0;
        const used = parseInt(data.used_services, 10) || 0;

        setPurchases(data.purchases || []);
        if (Array.isArray(data.service_claims)) {
          setServiceClaims(data.service_claims);
        } else {
          const derived =
            (data.purchases || []).map((p) => ({
              rp_id: p.rp_id,
              claim_id: p.claim_id,
              total_free_services: p.total_free_services ?? 0,
              used_services: p.used_services ?? 0,
              valid_until: p.valid_until,
            })) || [];
          setServiceClaims(derived);
        }

        setFreeServices(free);
        setUsedServices(used);
        setFreeServicesTotal(free + used);
      } catch (err) {
        console.error("Error fetching services:", err);
        setError(err?.message || "Failed to fetch services.");
      } finally {
        setLoading(false);
      }
    };

    fetchPurchases();
  }, [customerId, navigate]);

  useEffect(() => {
    const loadEligibility = async () => {
      if (!serviceClaims?.length) {
        setEligibilityMap({});
        return;
      }
      setEligLoading(true);
      try {
        const uniqueRpIds = [
          ...new Set(serviceClaims.map((c) => c.rp_id).filter(Boolean)),
        ];
        const entries = await Promise.all(
          uniqueRpIds.map(async (rpId) => {
            try {
              const data = await getRacketFreeEligibilityAdmin(
                Number(customerId),
                rpId
              );
              return [rpId, data];
            } catch {
              return [rpId, { eligible: true, nextEligibleDate: null }];
            }
          })
        );
        setEligibilityMap(Object.fromEntries(entries));
      } finally {
        setEligLoading(false);
      }
    };
    loadEligibility();
  }, [serviceClaims, customerId]);

  const totalPages = Math.ceil(serviceClaims.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const displayedClaims = serviceClaims.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

  const handleBookClick = (
    racketName,
    isFreeFinal,
    rp_id,
    claim_id,
    nextEligibleDate
  ) => {
    navigate("/dashboard/booking-form", {
      state: {
        customerId: Number(customerId),
        customerName,
        freeServices,
        usedServices,
        freeServicesTotal,
        racketName: racketName || "",
        isFree: !!isFreeFinal,
        rp_id: rp_id ?? null,
        claim_id: claim_id ?? null,
        nextEligibleDate,
      },
    });
  };

  return (
    <div className={styles.serviceDetails}>
      <div className={styles.buttonContainer}>
        <h2>Stringing Services</h2>
        <button className={styles.backButton} onClick={() => navigate(-1)}>
          Back
        </button>
      </div>

      {loading ? (
        <div className={styles.spinnerContainer}>
          <div className={styles.spinner}></div>
        </div>
      ) : error ? (
        <p className={styles.error}>{error}</p>
      ) : (
        <>
          <p className={styles.serviceMessage}>
            {customerName} has <strong>{freeServices}</strong> free stringing
            {freeServices === 1 ? " service" : " services"} available.
          </p>

          <div className={styles.tableContainer}>
            <table className={styles.detailsTable}>
              <thead>
                <tr>
                  <th>Racket Name</th>
                  <th>Total Free Services</th>
                  <th>Used Services</th>
                  <th>Valid Until</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {displayedClaims.length > 0 ? (
                  displayedClaims.map((claim, idx) => {
                    const purchase = purchases.find(
                      (p) => p.rp_id === claim.rp_id
                    );
                    const racketName =
                      purchase?.product_name ||
                      purchase?.racket_name ||
                      "Unknown Racket";
                    const total = claim.total_free_services ?? 0;
                    const used = claim.used_services ?? 0;
                    const remaining = total - used;
                    const hasUnitsRemaining = remaining > 0;

                    const elig = eligibilityMap[claim.rp_id];
                    const monthlyEligible = elig ? !!elig.eligible : true;
                    const nextDate = elig?.nextEligibleDate || null;

                    const isFreeFinal = hasUnitsRemaining && monthlyEligible;

                    return (
                      <tr key={`${claim.claim_id || claim.rp_id || idx}`}>
                        <td>{racketName}</td>
                        <td>{total}</td>
                        <td>{used}</td>
                        <td>
                          {claim.valid_until
                            ? new Date(claim.valid_until).toLocaleDateString(
                                "en-GB"
                              )
                            : "-"}
                        </td>
                        <td>
                          <button
                            className={
                              isFreeFinal
                                ? styles.bookButton
                                : styles.paidBookingButton
                            }
                            onClick={() =>
                              handleBookClick(
                                racketName,
                                isFreeFinal,
                                claim.rp_id,
                                claim.claim_id,
                                nextDate
                              )
                            }
                            title={
                              hasUnitsRemaining && !isFreeFinal && nextDate
                                ? `Next free available on ${nextDate}`
                                : undefined
                            }
                          >
                            {isFreeFinal ? "Redeem Free" : "Book Paid"}
                          </button>
                          {hasUnitsRemaining && !isFreeFinal && (
                            <div className={styles.cooldownNote}>
                              Next free stringing for this racket will be available after one
                              month{nextDate ? ` (on ${nextDate})` : ""}.
                            </div>
                          )}

                          {eligLoading && hasUnitsRemaining && (
                            <div className={styles.hintText}>
                              Checking eligibility…
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="5" className={styles.emptyMessage}>
                      No free services available.
                    </td>
                  </tr>
                )}

                <tr>
                  <td colSpan="5" className={styles.otherRacketRow}>
                    <strong>Need stringing for a different racket?</strong>
                    <button
                      className={styles.paidBookingButton}
                      onClick={() => handleBookClick("", false, null, null)}
                    >
                      Book it here
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {serviceClaims.length > itemsPerPage && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ServiceDetails;
